// ============================================
// CustomerContext.js — Auth centralizada de clientes
// Physiology New Look · Medusa v2
// ============================================

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { MEDUSA_API_URL, MEDUSA_API_KEY } from 'services/medusa/config';
import { devWarn, devError } from 'utils/devLog';

const CustomerContext = createContext();

// Helper: headers autenticados para Medusa v2
const authHeaders = (token) => ({
  'Content-Type': 'application/json',
  'x-publishable-api-key': MEDUSA_API_KEY,
  ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
});

export function CustomerProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem('medusa_token'));
  const [customer, setCustomer] = useState(() => {
    try {
      const saved = localStorage.getItem('medusa_customer');
      return saved ? JSON.parse(saved) : null;
    } catch (e) { devWarn('CustomerContext: error parsing cached customer', e); return null; }
  });
  const [loading, setLoading] = useState(true);
  const [isDistributor, setIsDistributor] = useState(false);
  // VIP: distribuidoras del grupo "Distribuidoras VIP" (promos exclusivas).
  const [isVip, setIsVip] = useState(false);
  const hasFetched = useRef(false);

  // ─── Logout ─────────────────────────────────────────────────────────────────
  const logout = useCallback(() => {
    localStorage.removeItem('medusa_token');
    localStorage.removeItem('medusa_customer');
    localStorage.removeItem('physToken');
    localStorage.removeItem('physUser');
    setToken(null);
    setCustomer(null);
    setIsDistributor(false);
    setIsVip(false);
  }, []);

  // ─── Estado distribuidora ───────────────────────────────────────────────────
  // Sólo true para clientas con solicitud APROBADA. Se usa para decidir si la
  // UI muestra el bloque dual "precio público | precio socio".
  const refreshDistributorStatus = useCallback(async (currentToken) => {
    const t = currentToken || token;
    if (!t) { setIsDistributor(false); setIsVip(false); return false; }
    try {
      const res = await fetch(`${MEDUSA_API_URL}/store/customers/me/distributor`, {
        method: 'GET',
        credentials: 'include',
        headers: authHeaders(t),
      });
      if (!res.ok) { setIsDistributor(false); setIsVip(false); return false; }
      const data = await res.json().catch(() => ({}));
      const flag = Boolean(data?.is_distributor);
      setIsDistributor(flag);
      setIsVip(flag && Boolean(data?.is_vip));
      return flag;
    } catch (e) {
      setIsDistributor(false);
      setIsVip(false);
      return false;
    }
  }, [token]);

  // ─── Obtener datos del cliente ───────────────────────────────────────────────
  const refreshCustomer = useCallback(async (currentToken, _isRetry = false) => {
    const t = currentToken || token;
    if (!t) return null;

    try {
      const res = await fetch(`${MEDUSA_API_URL}/store/customers/me`, {
        method: 'GET',
        credentials: 'include',
        headers: authHeaders(t),
      });

      if (res.ok) {
        const data = await res.json();
        const c = data.customer || data;
        setCustomer(c);
        setToken(t);
        localStorage.setItem('medusa_token', t);
        localStorage.setItem('medusa_customer', JSON.stringify(c));
        refreshDistributorStatus(t);
        return c;
      }

      // En 401/403 reintentamos con /auth/token/refresh una sola vez antes de cerrar sesión.
      // Medusa v2 emite primero un token de "auth-identity" que debe refrescarse para incluir el customer_id.
      if ((res.status === 401 || res.status === 403) && !_isRetry) {
        try {
          const refreshRes = await fetch(`${MEDUSA_API_URL}/auth/token/refresh`, {
            method: 'POST',
            credentials: 'include',
            headers: authHeaders(t),
          });
          if (refreshRes.ok) {
            const { token: refreshed } = await refreshRes.json();
            if (refreshed) {
              localStorage.setItem('medusa_token', refreshed);
              return refreshCustomer(refreshed, true);
            }
          }
        } catch (e) {
          // continuar al logout
        }
      }

      if (res.status === 401 || res.status === 403) {
        devWarn('CustomerContext: 401 tras refresh — cerrando sesión');
        setToken(null);
        setCustomer(null);
        setIsDistributor(false);
        setIsVip(false);
        localStorage.removeItem('medusa_token');
        localStorage.removeItem('medusa_customer');
        return null;
      }
    } catch (err) {
      devError('CustomerContext: error al obtener cliente', err);
    }
    return null;
  }, [token]);

  // ─── Verificar sesión al montar ──────────────────────────────────────────────
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    if (!token) {
      setLoading(false);
      return;
    }
    refreshCustomer(token).finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Actualizar datos del cliente ────────────────────────────────────────────
  const updateCustomer = useCallback(async (payload) => {
    if (!token) return { success: false, error: 'No autenticado' };

    try {
      const res = await fetch(`${MEDUSA_API_URL}/store/customers/me`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(token),
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return { success: false, error: err.message || `Error ${res.status}` };
      }

      const data = await res.json();
      const updated = data.customer || data;
      setCustomer(updated);
      localStorage.setItem('medusa_customer', JSON.stringify(updated));
      return { success: true, customer: updated };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [token]);

  // ─── Login (llamado desde LoginModal tras obtener token) ─────────────────────
  const setSession = useCallback((newToken, customerData) => {
    localStorage.setItem('medusa_token', newToken);
    localStorage.setItem('physToken', newToken);
    if (customerData) {
      localStorage.setItem('medusa_customer', JSON.stringify(customerData));
      localStorage.setItem('physUser', JSON.stringify({
        firstName: customerData.first_name,
        lastName: customerData.last_name,
        email: customerData.email,
      }));
      setCustomer(customerData);
    }
    setToken(newToken);
    refreshDistributorStatus(newToken);
  }, [refreshDistributorStatus]);

  return (
    <CustomerContext.Provider value={{
      token,
      customer,
      loading,
      isAuthenticated: !!token && !!customer,
      isDistributor,
      isVip,
      logout,
      refreshCustomer,
      refreshDistributorStatus,
      updateCustomer,
      setSession,
      authHeaders: () => authHeaders(token),
    }}>
      {children}
    </CustomerContext.Provider>
  );
}

export function useCustomer() {
  const context = useContext(CustomerContext);
  if (!context) {
    throw new Error('useCustomer debe usarse dentro de CustomerProvider');
  }
  return context;
}
